// OfferCountdownBig (iter 127) — eye-catching DD : HH : MM : SS box-grid
// countdown rendered directly below each plan card. Pulses red, optionally
// emits a tick/tock beep every second via Web Audio API (programmatic — no
// audio asset needed). Sound is OFF by default (browser autoplay policy)
// and can be enabled via the inline speaker toggle; preference is stored in
// sessionStorage so it persists across pills on the same page.
//
// Props:
//   • endsAt   — ISO datetime string. Returns null if empty/expired.
//   • testId   — base testid prefix (default: "offer-countdown-big").
//   • sound    — true → render the speaker toggle (default true). Set to
//                false on dense surfaces (admin) where the toggle is noise.
import { useEffect, useMemo, useRef, useState } from "react";
import { Volume2, VolumeX, Flame } from "lucide-react";

const SOUND_KEY = "mdm.tickSoundEnabled";

function parseTs(iso) {
  if (!iso || typeof iso !== "string") return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
}

function diff(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return {
    d: Math.floor(s / 86400),
    h: Math.floor((s % 86400) / 3600),
    m: Math.floor((s % 3600) / 60),
    s: s % 60,
    total: s,
  };
}

function pad(n) { return String(n).padStart(2, "0"); }

// Web Audio "tick" generator — short 1100Hz pulse for even seconds and
// 850Hz "tock" for odd seconds, ~25ms with sharp decay envelope.
function useTickPlayer(enabled) {
  const ctxRef = useRef(null);
  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof window === "undefined") return undefined;
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return undefined;
    if (!ctxRef.current) ctxRef.current = new Ctor();
    // Resume context inside a user-gesture-initiated effect.
    if (ctxRef.current.state === "suspended") {
      ctxRef.current.resume().catch(() => {});
    }
    return undefined;
  }, [enabled]);

  return (toneHigh) => {
    if (!enabled) return;
    const ctx = ctxRef.current;
    if (!ctx || ctx.state === "closed") return;
    try {
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      osc.frequency.setValueAtTime(toneHigh ? 1100 : 850, now);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.18, now + 0.002);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.07);
    } catch (e) { /* ignore — non-fatal */ }
  };
}

export default function OfferCountdownBig({ endsAt, testId = "offer-countdown-big", sound = true }) {
  const endTs = useMemo(() => parseTs(endsAt), [endsAt]);
  const [now, setNow] = useState(() => Date.now());
  const [muted, setMuted] = useState(() => {
    if (typeof window === "undefined") return true;
    try { return window.sessionStorage.getItem(SOUND_KEY) !== "1"; } catch (e) { return true; }
  });
  const playTick = useTickPlayer(!muted && !!endTs);

  // Re-tick every second so countdown stays accurate AND tick sound can fire.
  useEffect(() => {
    if (!endTs) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [endTs]);

  // Side-effect: play tick once per visible second (only when active + unmuted).
  const lastSecRef = useRef(-1);
  useEffect(() => {
    if (!endTs || muted) return;
    const remaining = endTs - now;
    if (remaining <= 0) return;
    const sec = Math.floor(remaining / 1000);
    if (sec === lastSecRef.current) return;
    lastSecRef.current = sec;
    playTick(sec % 2 === 0);
  }, [now, endTs, muted, playTick]);

  if (!endTs) return null;
  const remaining = endTs - now;
  if (remaining <= 0) {
    return (
      <div
        className="mt-3 flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-ink-200 bg-ink-50 px-3 py-2 text-xs font-semibold text-ink-500"
        data-testid={`${testId}-ended`}
      >
        ⌛ This offer has ended
      </div>
    );
  }
  const { d, h, m, s } = diff(remaining);
  const urgent = d === 0 && h < 1;            // pulse red in last hour
  const veryUrgent = d === 0 && h === 0 && m < 5; // shake in last 5m

  const toggleSound = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setMuted((prev) => {
      const next = !prev;
      try { window.sessionStorage.setItem(SOUND_KEY, next ? "0" : "1"); } catch (err) {}
      return next;
    });
  };

  const cell = (value, label) => (
    <div className="flex flex-col items-center">
      <span
        className={`min-w-[2.4rem] rounded-md bg-ink-900 px-2 py-1 text-center font-mono text-lg font-extrabold tabular-nums text-amber-300 shadow-inner sm:text-xl ${veryUrgent ? "offer-countdown-shake" : ""}`}
      >
        {pad(value)}
      </span>
      <span className="mt-0.5 text-[0.55rem] font-bold uppercase tracking-widest text-white/90">{label}</span>
    </div>
  );

  return (
    <div
      className={`offer-countdown-big mt-3 flex items-center justify-between gap-2 rounded-xl bg-gradient-to-r from-rose-600 via-red-600 to-orange-500 px-3 py-2 shadow-lg ring-2 ring-red-300/60 ${urgent ? "offer-countdown-pulse" : ""}`}
      data-testid={testId}
      role="timer"
      aria-label={`Offer ends in ${d} days ${h} hours ${m} minutes ${s} seconds`}
    >
      <div className="flex items-center gap-1.5 text-white">
        <Flame className="h-4 w-4 shrink-0 text-amber-300 drop-shadow" />
        <span className="hidden text-[0.65rem] font-extrabold uppercase tracking-[0.18em] text-white sm:inline">Ends in</span>
      </div>
      <div className="flex items-center gap-1.5">
        {cell(d, "D")}
        <span className="text-base font-extrabold text-amber-300">:</span>
        {cell(h, "H")}
        <span className="text-base font-extrabold text-amber-300">:</span>
        {cell(m, "M")}
        <span className="text-base font-extrabold text-amber-300">:</span>
        {cell(s, "S")}
      </div>
      {sound && (
        <button
          type="button"
          onClick={toggleSound}
          className="rounded-full bg-white/15 p-1.5 text-white transition hover:bg-white/30"
          title={muted ? "Enable tick sound" : "Mute tick sound"}
          aria-label={muted ? "Enable tick sound" : "Mute tick sound"}
          data-testid={`${testId}-sound-toggle`}
        >
          {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
        </button>
      )}
    </div>
  );
}
